import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const COOLDOWN_SECONDS = 60

export default function VerifyEmailPage() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [cooldown, setCooldown] = useState(0)
  const [status, setStatus] = useState(null) // 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('')

  // Redirect if already verified
  useEffect(() => {
    if (user?.email_confirmed_at) {
      navigate('/home', { replace: true })
    }
  }, [user, navigate])

  // Countdown timer
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  const handleResend = useCallback(async () => {
    if (!user?.email || cooldown > 0) return
    setStatus(null)
    setErrorMsg('')
    const { error } = await supabase.auth.resend({ type: 'signup', email: user.email })
    if (error) {
      setStatus('error')
      setErrorMsg(error.message)
    } else {
      setStatus('sent')
      setCooldown(COOLDOWN_SECONDS)
    }
  }, [user, cooldown])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-surface rounded-2xl shadow-sm border border-border p-8 w-full max-w-md text-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-text-primary mb-2">Verify your email</h1>
        <p className="text-text-secondary text-sm mb-1">
          We sent a verification link to:
        </p>
        <p className="font-semibold text-text-primary mb-6 break-all">{user?.email}</p>

        <p className="text-text-muted text-sm mb-6">
          Click the link in the email to activate your account. Check your spam folder if you don't see it.
        </p>

        {status === 'sent' && (
          <div className="bg-green-50 text-green-700 rounded-lg px-4 py-3 text-sm mb-4">
            Verification email sent! Check your inbox.
          </div>
        )}
        {status === 'error' && (
          <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
            {errorMsg || 'Failed to resend. Please try again.'}
          </div>
        )}

        <button
          onClick={handleResend}
          disabled={cooldown > 0}
          className="w-full bg-primary text-white rounded-xl py-3 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-opacity mb-3"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification email'}
        </button>

        <div className="flex flex-col gap-2 text-sm text-text-muted">
          <button
            onClick={() => signOut().then(() => navigate('/login'))}
            className="underline underline-offset-2 hover:text-text-secondary"
          >
            Wrong email? Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
